(() => {
  if (window.Wood24OrderPreview) return;
  const states = new WeakMap();
  const value = (form, key) => form.querySelector(`[name="properties[${key}]"]`)?.value || '';
  function init(form) {
    if (states.has(form)) return states.get(form);
    const root = form.querySelector('.wood24-personalize');
    if (!root) return null;
    const board = root.querySelector('.wood24-live-board');
    const canvas = document.createElement('canvas');
    canvas.style.cssText = 'width:100%;height:auto;display:block';
    canvas.setAttribute('role', 'img');
    canvas.setAttribute('aria-label', 'Deine aktuelle Gravurgestaltung');
    board.style.display = 'block';
    board.replaceChildren(canvas);
    const state = {root, board, canvas, paths:'', motif:'', revision:0, pending:null};
    states.set(form, state);
    form.addEventListener('wood24:symbolchange', event => {
      state.paths = event.detail?.paths || '';
      state.motif = event.detail?.name || '';
      refresh(form, state);
    });
    root.addEventListener('input', () => refresh(form, state));
    root.addEventListener('change', () => refresh(form, state));
    refresh(form, state);
    return state;
  }
  function snapshot(form, state) {
    const motif = value(form, 'Grafiksymbol');
    if (motif && motif !== state.motif) {
      const svg = state.root.querySelector('.wood24-symbol-tile[aria-pressed="true"] svg');
      if (!svg) throw new Error('Bitte wähle dein Motiv erneut aus.');
      state.paths = svg.innerHTML;
      state.motif = motif;
    }
    const font = value(form, 'Schriftart');
    return {
      version:'wood24-preview-2026-10-21',
      text:value(form,'Wunschgravur'), date:value(form,'Gravurdatum'),
      qr:value(form,'QR-Code-Link'), font,
      family:/Schreibschrift|Verspielt/.test(font)?'cursive':/Klassisch|Rustikal/.test(font)?'Georgia,serif':'Arial,sans-serif',
      textSize:Number(state.root.querySelector('.wood24-text-size')?.value || 18),
      motifSize:Number(state.root.querySelector('.wood24-motif-size')?.value || 42),
      motif, paths:motif?state.paths:'', notes:value(form,'Gestaltungshinweise'),
      upload:state.root.querySelector('input[name="properties[Grafikdatei]"]')?.files?.[0]?.name || '',
      variant:form.querySelector('[name="id"]')?.value || ''
    };
  }
  function wrap(context, text, maxWidth) {
    const lines=[];
    for (const paragraph of text.split(/\r?\n/)) {
      if (!paragraph) { lines.push(''); continue; }
      let line='';
      for (const word of paragraph.split(/(\s+)/)) {
        if (context.measureText(line+word).width<=maxWidth) { line+=word; continue; }
        if (line.trim()) { lines.push(line.trimEnd()); line=''; }
        for (const char of word.trimStart()) {
          if (line && context.measureText(line+char).width>maxWidth) {lines.push(line);line='';}
          line+=char;
        }
      }
      lines.push(line.trimEnd());
    }
    return lines;
  }
  async function render(data) {
    await document.fonts.ready;
    const scale=2, width=640, canvas=document.createElement('canvas');
    let context=canvas.getContext('2d');
    context.font=`${data.textSize*scale}px ${data.family}`;
    const date=data.date?data.date.split('-').reverse().join('.'):'';
    const text=[data.text || (data.qr?'QR-Code-Ziel: '+data.qr:''),date].filter(Boolean).join('\n');
    const lines=wrap(context,text || 'Hier erscheint dein Wunschtext',width-64);
    const lineHeight=data.textSize*scale*1.35;
    const motifHeight=data.paths?data.motifSize*scale+20:0;
    const footer=data.upload?'Upload separat beigefügt – nicht in dieser Vorschau':'';
    canvas.width=width;canvas.height=Math.ceil(Math.max(220,64+motifHeight+lines.length*lineHeight+(footer?44:0)));
    context=canvas.getContext('2d');
    const background=context.createLinearGradient(0,0,width,canvas.height);
    background.addColorStop(0,'#e9d3af');background.addColorStop(1,'#cfab7d');
    context.fillStyle=background;context.fillRect(0,0,width,canvas.height);
    let y=32;
    if (data.paths) {
      const source=`<svg xmlns="http://www.w3.org/2000/svg" width="${data.motifSize*scale}" height="${data.motifSize*scale}" viewBox="0 0 24 24" fill="none" stroke="#382b1c" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">${data.paths}</svg>`;
      const image=new Image();
      const url=URL.createObjectURL(new Blob([source],{type:'image/svg+xml'}));
      try {
        await new Promise((resolve,reject)=>{image.onload=resolve;image.onerror=()=>reject(new Error('Das Motiv konnte nicht geladen werden. Bitte erneut auswählen.'));image.src=url;});
        context.drawImage(image,(width-data.motifSize*scale)/2,y,data.motifSize*scale,data.motifSize*scale);
      } finally {URL.revokeObjectURL(url);}
      y+=motifHeight;
    }
    context.fillStyle='#382b1c';context.textAlign='center';context.textBaseline='top';
    context.font=`${data.textSize*scale}px ${data.family}`;
    for (const line of lines) {context.fillText(line,width/2,y);y+=lineHeight;}
    if (footer) {context.font='18px Arial';context.fillText(footer,width/2,y+16);}
    return canvas;
  }
  function refresh(form,state) {
    const revision=++state.revision;
    const file=state.root.querySelector('.wood24-preview-file');
    if(file) file.value='';
    state.root.querySelector('.wood24-preview-status').textContent='Vorschau wird aktualisiert …';
    state.pending=(async()=>{
      const data=snapshot(form,state), canvas=await render(data);
      if(revision!==state.revision)return null;
      state.canvas.width=canvas.width;state.canvas.height=canvas.height;
      state.canvas.getContext('2d').drawImage(canvas,0,0);
      state.canvas.setAttribute('aria-label', [data.text,data.date,data.motif].filter(Boolean).join(' – ') || 'Gravurvorschau');
      state.root.querySelector('.wood24-preview-status').textContent='Diese Vorschau wird mit deiner Bestellung gespeichert.';
      return {data,canvas};
    })().catch(error=>{
      if(revision===state.revision)state.root.querySelector('.wood24-preview-status').textContent=error.message;
      return {error};
    });
    return state.pending;
  }
  async function prepare(form) {
    const state=init(form);
    if(!state)throw new Error('Die Personalisierung konnte nicht geladen werden. Bitte lade die Seite neu.');
    const result=await refresh(form,state);
    if(!result || result.error)throw result?.error || new Error('Deine Gestaltung wurde geändert. Bitte erneut in den Warenkorb legen.');
    const revision=state.revision;
    const blob=await new Promise(resolve=>result.canvas.toBlob(resolve,'image/png'));
    if(!blob)throw new Error('Die Vorschau konnte nicht gespeichert werden. Bitte erneut versuchen.');
    if(revision!==state.revision || JSON.stringify(snapshot(form,state))!==JSON.stringify(result.data))throw new Error('Deine Gestaltung wurde geändert. Bitte erneut in den Warenkorb legen.');
    const transfer=new DataTransfer();
    transfer.items.add(new File([blob],`WOOD24-Gestaltung-${Date.now()}.png`,{type:'image/png'}));
    const file=state.root.querySelector('.wood24-preview-file');
    file.files=transfer.files;
    if(file.files.length!==1)throw new Error('Dein Browser konnte die Vorschau nicht anhängen. Bitte verwende einen aktuellen Browser.');
    let details=form.querySelector('[name="properties[_Gestaltungsdaten]"]');
    if(!details){details=document.createElement('input');details.type='hidden';details.name='properties[_Gestaltungsdaten]';form.append(details);}
    details.value=JSON.stringify(result.data);
    let motifId=form.querySelector('[name="properties[Motivkennung]"]');
    if(!motifId){motifId=document.createElement('input');motifId.type='hidden';motifId.name='properties[Motivkennung]';form.append(motifId);}
    motifId.value=result.data.motif?'W24-256-v1 / '+result.data.motif:'';
    return result;
  }
  window.Wood24OrderPreview={prepare};
  const scan=()=>document.querySelectorAll('form').forEach(form=>{if(form.querySelector('.wood24-personalize'))init(form);});
  scan();
  window.addEventListener('pageshow', event => {
    if (!event.persisted) return;
    document.querySelectorAll('form').forEach(form => {
      if (!form.querySelector('.wood24-personalize')) return;
      form.querySelector('[type="submit"]')?.removeAttribute('aria-disabled');
      const state=init(form);
      if(state)refresh(form,state);
    });
  });
  document.addEventListener('shopify:section:load',scan);
  new MutationObserver(scan).observe(document.body,{childList:true,subtree:true});
})();
